import type { ComponentType } from "react";

import type { BaseComponentProps } from "../../types";

/**
 * Registry mapping custom component names to their React implementations.
 * Custom components handle complex imperative UX that can't be expressed declaratively.
 *
 * The map is a mutable module-level singleton populated by side-effecting
 * `registerCustomComponent` calls at module import time (see e.g. the direct-apply
 * `ResyncButton` index). Production bundles import each registrar exactly once, but
 * tests that import the same registrar across files would silently overwrite — the
 * console warning below makes that visible so we don't debug it later.
 */
const customComponentMap: Record<string, ComponentType<BaseComponentProps>> = {};

/**
 * Register a custom component by name. Logs a warning if `name` is already registered
 * so accidental double-registration (typically from test setup that imports a registrar
 * twice) surfaces in the console instead of being silently overridden.
 */
export function registerCustomComponent(
  name: string,
  component: ComponentType<BaseComponentProps>,
): void {
  if (customComponentMap[name] && customComponentMap[name] !== component) {
    console.warn(
      `[customComponentMap] Re-registering custom component "${name}". ` +
        "This usually means the registrar module was imported twice.",
    );
  }
  customComponentMap[name] = component;
}

/**
 * Get a registered custom component by name.
 */
export function getCustomComponent(
  name: string,
): ComponentType<BaseComponentProps> | undefined {
  return customComponentMap[name];
}
